/* =========================================================================
   Tile Touch — room planner / quantity calculator

   Does on the page what the sales team does on the phone: takes room
   dimensions, converts to m2, adds a wastage allowance, rounds up to whole
   boxes, prices it, checks it against live stock, and puts it in the cart.

   Reads coverage from data-sqm-per-box. Price and inventory come from the
   PDP's [data-variants-json] payload so the numbers follow variant changes
   instead of going stale.

   Writes the box count into the PDP form's input[name="quantity"] — it does
   not create a second form or bind a second submit handler.
   ========================================================================= */
(function () {
  'use strict';

  var AREA_TYPES = ['Floor', 'Wall', 'Splashback', 'Feature wall', 'Other'];

  /* Freight bands, exactly as the business quotes them, cross-checked against
     the rates Shopify actually returns at checkout (1 box to Sydney = $80,
     13 boxes = $150, Melbourne/Brisbane = $165, Perth = $230, regional NSW
     = $195). Delivery windows come from the FAQ page.

     `small` applies to orders under 30kg. We show the band that matches the
     order and say plainly that regional and remote cost more, rather than
     quoting one number that turns out to be wrong at checkout. */
  var SMALL_ORDER_KG = 30;

  var STATES = [
    { code: 'NT',  name: 'NT',  days: '7–12 business days',
      ranges: [[800, 999]], flat: 290, remote: 390 },
    { code: 'ACT', name: 'ACT', days: '3–5 business days',
      ranges: [[200, 299], [2600, 2618], [2900, 2920]],
      small: 125, metro: 165, nonMetro: 210, regional: 235, remote: 290 },
    { code: 'NSW', name: 'NSW', days: '3–5 business days',
      ranges: [[1000, 2599], [2619, 2899], [2921, 2999]],
      small: 80, metro: 150, nonMetro: 175, regional: 195, remote: 290 },
    { code: 'VIC', name: 'VIC', days: '3–5 business days',
      ranges: [[3000, 3999], [8000, 8999]],
      small: 125, metro: 165, nonMetro: 210, regional: 235, remote: 290 },
    { code: 'QLD', name: 'QLD', days: '4–6 business days',
      ranges: [[4000, 4999], [9000, 9999]],
      small: 125, metro: 165, nonMetro: 210, regional: 235, remote: 290 },
    { code: 'SA',  name: 'SA',  days: '6–7 business days',
      ranges: [[5000, 5999]],
      small: 150, metro: 190, regional: 305, remote: 380 },
    { code: 'WA',  name: 'WA',  days: '7–12 business days',
      ranges: [[6000, 6797], [6800, 6999]],
      metro: 230, regional: 310, remote: 390 },
    { code: 'TAS', name: 'TAS', days: '7–12 business days',
      ranges: [[7000, 7999]], flat: 310, remote: 390 }
  ];

  /* Capital-city metro postcode ranges. Deliberately conservative: if a
     postcode is not clearly metro we quote the wider band rather than promise
     a metro price the checkout will not honour. */
  var METRO = {
    NSW: [[1000, 1920], [2000, 2249], [2555, 2574], [2740, 2770]],
    VIC: [[3000, 3207], [3335, 3341], [3425, 3443], [3750, 3811], [8000, 8999]],
    QLD: [[4000, 4207], [4300, 4305], [9000, 9999]],
    SA:  [[5000, 5199]],
    WA:  [[6000, 6199]],
    ACT: [[200, 299], [2600, 2618], [2900, 2920]]
  };

  function isMetro(code, pc) {
    var r = METRO[code];
    if (!r) return false;
    for (var i = 0; i < r.length; i++) if (pc >= r[i][0] && pc <= r[i][1]) return true;
    return false;
  }

  /** Returns {label, detail} describing what delivery will cost and when. */
  function deliveryQuote(st, pc, kg) {
    if (!st) return null;
    var small = kg > 0 && kg < SMALL_ORDER_KG;

    // NT and TAS are a single price regardless of metro/regional.
    if (st.flat) {
      return {
        price: st.flat,
        note: 'Remote areas $' + st.remote + '.'
      };
    }

    if (isMetro(st.code, pc)) {
      var price = (small && st.small) ? st.small : st.metro;
      return {
        price: price,
        note: (small && st.small)
          ? 'Under ' + SMALL_ORDER_KG + 'kg, so you get the small-order rate.'
          : 'Metro rate.'
      };
    }

    // Outside the capital: quote the honest range for that state.
    var low = st.nonMetro || st.regional || st.metro;
    var high = st.remote;
    return {
      range: [low, high],
      note: 'Outside the metro area — the exact band is confirmed at checkout.'
    };
  }

  function stateFor(postcode) {
    var pc = parseInt(postcode, 10);
    if (!isFinite(pc)) return null;
    for (var i = 0; i < STATES.length; i++) {
      for (var j = 0; j < STATES[i].ranges.length; j++) {
        if (pc >= STATES[i].ranges[j][0] && pc <= STATES[i].ranges[j][1]) return STATES[i];
      }
    }
    return null;
  }

  function num(v) {
    var n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : 0;
  }

  function money(cents) {
    var dollars = cents / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2
      }).format(dollars);
    } catch (e) {
      return '$' + dollars.toFixed(2);
    }
  }

  function round(n, dp) {
    var f = Math.pow(10, dp || 2);
    return Math.round(n * f) / f;
  }

  function initCalc(root) {
    var sqmPerBox = num(root.getAttribute('data-sqm-per-box'));
    if (!sqmPerBox) return; // no coverage data: leave the plain stepper alone

    var wastage = num(root.getAttribute('data-wastage-default')) || 10;
    var productTitle = root.getAttribute('data-product-title') || 'this tile';
    var productUrl = root.getAttribute('data-product-url') || '';
    var salesEmail = root.getAttribute('data-sales-email') || 'sales@tiletouch.com.au';

    var areasWrap = root.querySelector('[data-qc-areas]');
    var addAreaBtn = root.querySelector('[data-qc-add-area]');
    var modeToggle = root.querySelector('[data-qc-toggle-mode]');
    var directWrap = root.querySelector('[data-qc-direct]');
    var directInput = root.querySelector('[data-qc-direct-input]');
    var wasteBtns = root.querySelectorAll('[data-qc-waste]');
    var result = root.querySelector('[data-qc-result]');
    var boxesOut = root.querySelector('[data-qc-boxes-out]');
    var boxesWord = root.querySelector('[data-qc-boxes-word]');
    var breakdown = root.querySelector('[data-qc-breakdown]');
    var totalOut = root.querySelector('[data-qc-total]');
    var unitOut = root.querySelector('[data-qc-unit]');
    var stockOut = root.querySelector('[data-qc-stock]');
    var ctaCount = root.querySelector('[data-qc-cta-count]');
    var addBtn = root.querySelector('[data-qc-add]');
    var emailLink = root.querySelector('[data-qc-email]');

    var form = root.closest('form[data-product-form]') ||
               document.querySelector('form[data-product-form]');
    var qtyInput = form ? form.querySelector('input[name="quantity"]') : null;

    var directMode = false;
    var postcode = '';
    var renderDelivery = function(){};
    var last = { boxes: 0, area: 0, buyArea: 0 };

    /* ---- variant price + inventory, kept in step with the selector ---- */

    function currentVariant() {
      var json = document.querySelector('[data-variants-json]');
      var idEl = document.querySelector('[data-variant-id]');
      if (!json || !idEl) return null;
      var list;
      try { list = JSON.parse(json.textContent); } catch (e) { return null; }
      var id = parseInt(idEl.value, 10);
      for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
      return list[0] || null;
    }

    /* ---------------------------- area rows ---------------------------- */

    function areaRow(typeIndex) {
      var row = document.createElement('div');
      row.className = 'qc__area';
      row.setAttribute('data-qc-area', '');

      var opts = AREA_TYPES.map(function (t, i) {
        return '<option value="' + t + '"' + (i === typeIndex ? ' selected' : '') + '>' + t + '</option>';
      }).join('');

      row.innerHTML =
        '<select class="qc__select" data-qc-type aria-label="Area type">' + opts + '</select>' +
        '<span class="qc__dims">' +
          '<input class="qc__input qc__input--dim" type="number" inputmode="decimal" min="0" step="0.01" placeholder="0.0" data-qc-len aria-label="Length in metres">' +
          '<span class="qc__x" aria-hidden="true">&times;</span>' +
          '<input class="qc__input qc__input--dim" type="number" inputmode="decimal" min="0" step="0.01" placeholder="0.0" data-qc-wid aria-label="Width in metres">' +
          '<span class="qc__unit">m</span>' +
        '</span>' +
        '<span class="qc__area-out" data-qc-area-out>&mdash;</span>' +
        '<button type="button" class="qc__remove" data-qc-remove aria-label="Remove this area">&times;</button>';

      row.querySelector('[data-qc-remove]').addEventListener('click', function () {
        row.remove();
        syncRemoveButtons();
        recalc();
      });
      row.addEventListener('input', recalc);
      row.addEventListener('change', recalc);
      return row;
    }

    // One area needs no remove button; the control only appears once it can act.
    function syncRemoveButtons() {
      var rows = areasWrap.querySelectorAll('[data-qc-area]');
      for (var i = 0; i < rows.length; i++) {
        rows[i].querySelector('[data-qc-remove]').hidden = rows.length < 2;
      }
    }

    function areaBreakdown() {
      var rows = areasWrap.querySelectorAll('[data-qc-area]');
      var total = 0;
      var parts = [];
      for (var i = 0; i < rows.length; i++) {
        var l = num(rows[i].querySelector('[data-qc-len]').value);
        var w = num(rows[i].querySelector('[data-qc-wid]').value);
        var a = l * w;
        var out = rows[i].querySelector('[data-qc-area-out]');
        out.textContent = a > 0 ? round(a, 2) + ' m²' : '—';
        if (a > 0) {
          total += a;
          parts.push(rows[i].querySelector('[data-qc-type]').value + ' ' +
                     round(l, 2) + '×' + round(w, 2) + 'm');
        }
      }
      return { total: total, parts: parts };
    }

    /* ------------------------------ maths ------------------------------ */

    function recalc() {
      var area = 0;
      var parts = [];

      if (directMode) {
        area = num(directInput && directInput.value);
      } else {
        var b = areaBreakdown();
        area = b.total;
        parts = b.parts;
      }

      if (!area) {
        result.hidden = true;
        setQty(0);
        last = { boxes: 0, area: 0, buyArea: 0, parts: [] };
        return;
      }

      var buyArea = area * (1 + wastage / 100);
      var boxes = Math.ceil(buyArea / sqmPerBox);
      var covers = boxes * sqmPerBox;

      var v = currentVariant();
      var unitCents = v ? v.price : 0;
      var totalCents = unitCents * boxes;

      result.hidden = false;
      boxesOut.textContent = boxes;
      boxesWord.textContent = boxes === 1 ? 'box' : 'boxes';
      ctaCount.textContent = boxes;

      breakdown.textContent =
        'covers ' + round(covers, 2) + 'm² — you\'re tiling ' + round(area, 2) + 'm²' +
        (wastage > 0 ? ' plus ' + wastage + '% waste' : '');

      totalOut.textContent = unitCents ? money(totalCents) : '—';
      unitOut.textContent = unitCents ? money(unitCents) + ' per box' : '';

      renderStock(v, boxes);
      setQty(boxes);

      last = { boxes: boxes, area: area, buyArea: buyArea, covers: covers, parts: parts, total: totalCents };
      renderDelivery();
      updateEmail();
    }

    // Tells them the truth about availability before they commit, rather than
    // at checkout. A short order is a phone call either way — better it be a
    // deliberate one.
    function renderStock(v, boxes) {
      if (!v || !v.tracked) { stockOut.hidden = true; return; }
      stockOut.hidden = false;
      stockOut.className = 'qc__stock';
      var inv = v.inv || 0;

      if (inv >= boxes) {
        stockOut.classList.add('qc__stock--in');
        stockOut.textContent = 'All ' + boxes + ' ' + (boxes === 1 ? 'box' : 'boxes') +
          ' in stock now — ready to ship';
      } else if (inv > 0) {
        stockOut.classList.add('qc__stock--low');
        stockOut.textContent = 'Only ' + inv + ' of the ' + boxes + ' you need are in stock. ' +
          'Send Charles your numbers below and he\'ll check the next shipment.';
      } else {
        stockOut.classList.add('qc__stock--out');
        stockOut.textContent = 'Out of stock right now — Charles can tell you when it lands.';
      }
    }

    function setQty(boxes) {
      if (!qtyInput) return;
      if (boxes > 0) {
        qtyInput.value = boxes;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ---- handing a fully-worked quote to the sales team, not a "hi" ---- */
    function updateEmail() {
      if (!emailLink) return;
      var lines = [
        'Hi Charles,',
        '',
        'Could you check this for me?',
        '',
        'Tile: ' + productTitle,
        productUrl,
        ''
      ];
      if (last.parts && last.parts.length) {
        lines.push('Areas:');
        for (var i = 0; i < last.parts.length; i++) lines.push('  - ' + last.parts[i]);
      }
      lines.push('Total to tile: ' + round(last.area, 2) + ' m2');
      lines.push('Wastage allowed: ' + wastage + '%');
      lines.push('Boxes worked out: ' + last.boxes + ' (covers ' + round(last.covers || 0, 2) + ' m2)');
      if (last.total) lines.push('Approx total: ' + money(last.total));
      lines.push('Delivering to postcode: ' + (postcode || '(not given)'));
      if (deliveryOut && postcode) lines.push('Delivery shown: ' + deliveryOut.textContent.replace(/s+/g, ' ').trim());
      lines.push('', 'Thanks');

      emailLink.href = 'mailto:' + salesEmail +
        '?subject=' + encodeURIComponent('Quantity check — ' + productTitle) +
        '&body=' + encodeURIComponent(lines.join('\n'));
    }

    /* ----------------------------- wiring ----------------------------- */

    for (var i = 0; i < wasteBtns.length; i++) {
      wasteBtns[i].addEventListener('click', function () {
        wastage = num(this.getAttribute('data-qc-waste'));
        if (this.getAttribute('data-qc-waste') === '0') wastage = 0;
        for (var j = 0; j < wasteBtns.length; j++) {
          wasteBtns[j].setAttribute('aria-pressed', wasteBtns[j] === this ? 'true' : 'false');
        }
        recalc();
      });
    }

    addAreaBtn.addEventListener('click', function () {
      var n = areasWrap.querySelectorAll('[data-qc-area]').length;
      areasWrap.appendChild(areaRow(n === 0 ? 0 : 1));
      syncRemoveButtons();
      var added = areasWrap.lastElementChild.querySelector('[data-qc-len]');
      if (added) added.focus();
    });

    modeToggle.addEventListener('click', function () {
      directMode = !directMode;
      directWrap.hidden = !directMode;
      areasWrap.hidden = directMode;
      addAreaBtn.hidden = directMode;
      this.textContent = directMode ? 'Work it out from room sizes' : 'I already know my m²';
      recalc();
      var focusTarget = directMode ? directInput : areasWrap.querySelector('[data-qc-len]');
      if (focusTarget) focusTarget.focus();
    });

    if (directInput) directInput.addEventListener('input', recalc);

    var postcodeInput = root.querySelector('[data-qc-postcode]');
    var deliveryOut = root.querySelector('[data-qc-delivery]');

    // Kept as its own function so it re-runs when the box count changes too —
    // crossing 30kg moves you off the small-order rate.
    renderDelivery = function () {
      if (!deliveryOut) return;
      if (!postcode || postcode.length < 4) {
        deliveryOut.textContent = 'Flat-rate delivery Australia-wide, or free pick-up from Waterloo';
        deliveryOut.classList.remove('is-known');
        return;
      }
      var st = stateFor(postcode);
      if (!st) {
        deliveryOut.textContent = 'That postcode doesn\'t look right — check it, or we\'ll confirm at checkout.';
        deliveryOut.classList.remove('is-known');
        return;
      }
      var v = currentVariant();
      var kg = v && v.weight ? (v.weight * last.boxes) / 1000 : 0;
      var q = deliveryQuote(st, parseInt(postcode, 10), kg);
      var price = q.range
        ? '$' + q.range[0] + '–$' + q.range[1]
        : '$' + q.price;
      deliveryOut.innerHTML =
        '<strong>' + st.name + ' &middot; ' + price + ' delivery</strong> &middot; ' +
        st.days + ' from dispatch' +
        (kg ? ' &middot; approx ' + Math.round(kg) + 'kg' : '') +
        '<br><span class="qc__delivery-note">' + q.note +
        ' Free pick-up from Waterloo.</span>';
      deliveryOut.classList.add('is-known');
    };
    if (postcodeInput && deliveryOut) {
      postcodeInput.addEventListener('input', function () {
        var raw = this.value.replace(/\D/g, '').slice(0, 4);
        this.value = raw;
        if (raw.length < 4) {
          deliveryOut.textContent = 'Flat-rate delivery Australia-wide, from $80';
          deliveryOut.classList.remove('is-known');
          postcode = '';
          updateEmail();
          return;
        }
        postcode = raw;
        renderDelivery();
        updateEmail();
      });
    }

    // One click from "how much do I need" to "it's in the cart".
    if (addBtn && form) {
      addBtn.addEventListener('click', function () {
        if (!last.boxes) return;
        setQty(last.boxes);
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          var submit = form.querySelector('[data-add-btn]');
          if (submit) submit.click();
        }
      });
    }

    // Price and stock must follow the variant selector.
    document.addEventListener('variant:change', recalc);
    var idEl = document.querySelector('[data-variant-id]');
    if (idEl) idEl.addEventListener('change', recalc);

    areasWrap.appendChild(areaRow(0));
    syncRemoveButtons();
    recalc();
  }

  function init() {
    var roots = document.querySelectorAll('[data-quantity-calc]');
    for (var i = 0; i < roots.length; i++) initCalc(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
